import React, { useState, useEffect, useRef } from "react";
import { MoreHorizontal } from "lucide-react";
import ColumnWidthToggleButton from "../Buttons/ColumnWidthToggleButton";
import ColumnMenuDropdown from "../Dropdowns/ColumnMenuDropdown";
import { handleFilterChange } from "../../utils/TableFilterService";

/**
 * EnhancedTableControlPanel
 *
 * A floating control panel for a table column header, providing user controls for:
 *  - toggling visibility of predefined columns
 *  - adjusting column width (narrow/expand)
 *  - sorting the column
 *  - pinning/unpinning the column
 *  - filtering column data
 *  - hiding the current column
 *  - opening a dropdown menu with additional actions
 *
 * Designed for use in advanced table/grid UIs to allow users to customize columns interactively.
 *
 * @component
 * @param {Object} props - All props listed below are required unless otherwise noted.
 * @param {string} props.columnId - The unique ID of the column this panel controls.
 * @param {Function} props.onToggleColumns - Callback to toggle visibility of predefined columns.
 *   Receives (newState: boolean).
 * @param {Function} props.onToggleColumnWidth - Callback to toggle column width (narrow/expand).
 *   Receives (columnId: string, newState: boolean).
 * @param {Object} props.columnVisibility - Object mapping column IDs to visibility booleans.
 * @param {Function} props.setColumnVisibility - Setter to update column visibility object.
 *   Receives (prevState: object) => object.
 * @param {boolean} [props.isActive=false] - If true, the column is in "narrow" mode.
 * @param {Function} props.setIsPanelSticky - Callback to make the panel sticky/fixed after interaction.
 * @param {Function} props.onSort - Callback for sort actions; receives a sort object { id, desc }.
 * @param {boolean} [props.isPinned=false] - If true, the column is currently pinned.
 * @param {Function} props.onPinColumn - Callback to pin/unpin the column.
 *   Receives (columnId: string, pinState: boolean).
 * @param {Object} [props.allFilterOptions={}] - Map of columnId → filter options array.
 * @param {Object} props.filters - Current filters applied to the table.
 * @param {Function} props.setFilters - Setter to update the filters object.
 * @param {Object} [props.setterFunctions={}] - Map of columnId → specific setter functions for filter selection.
 * @param {Object} props.hoverManager - Object with mouse enter/leave handlers for mouseover behavior.
 *   Expects: { handleHeaderMouseEnter: (e, colId) => {}, handleHeaderMouseLeave: (e, colId) => {} }
 * @returns {JSX.Element}
 */
const EnhancedTableControlPanel = ({
  columnId,
  onToggleColumns,
  onToggleColumnWidth,
  setColumnVisibility,
  isActive = false,
  setIsPanelSticky,
  onSort,
  isPinned = false,
  onPinColumn,
  allFilterOptions = {},
  filters,
  setFilters,
  setterFunctions = {},
  hoverManager,
}) => {
  // State for visibility of predefined columns
  const [columnsHidden, setColumnsHidden] = useState(false);
  // State to track menu dropdown open/close
  const [menuOpen, setMenuOpen] = useState(false);
  // State for visual feedback on columns toggle button
  const [columnsButtonActive, setColumnsButtonActive] = useState(false);

  // Ref to persist the last valid columnId across re-renders
  const lastValidColumnIdRef = useRef(columnId);

  // Update last valid columnId whenever columnId changes to a non-falsy value
  useEffect(() => {
    if (columnId) {
      lastValidColumnIdRef.current = columnId;
    }
  }, [columnId]);

  /**
   * Compute tailwind classes for menu button based on active/open state.
   * @param {boolean} isActiveState
   * @returns {string}
   */
  const getButtonClasses = (isActiveState) =>
    `p-2 rounded-md transition-colors ${
      isActiveState ? "bg-[#0832DE] text-white" : "text-white"
    }`;

  /**
   * Toggle visibility of predefined columns.
   * Updates local and parent state, and makes panel sticky.
   */
  const handleToggleColumns = () => {
    const newState = !columnsHidden;
    setColumnsHidden(newState);
    setColumnsButtonActive(newState); // Button is active when columns are NOT hidden

    if (typeof onToggleColumns === "function") {
      onToggleColumns(newState);
    }
    if (setIsPanelSticky) setIsPanelSticky(true);
  };

  /**
   * Toggle the width (narrow/expand) of the current column.
   * Always passes a valid columnId to the callback.
   * @param {boolean} newState - Whether to set column as narrow.
   */
  const handleToggleColumnWidth = (newState) => {
    const targetColumnId = columnId || lastValidColumnIdRef.current;
    if (!targetColumnId) {
      console.warn("Cannot toggle column width: no valid column ID available");
      return;
    }
    if (typeof onToggleColumnWidth === "function") {
      onToggleColumnWidth(targetColumnId, newState);
    }
    if (setIsPanelSticky) setIsPanelSticky(true);
  };

  /**
   * Hide the current column by setting its visibility to false.
   * @param {string|null} id - Optionally provide a columnId, otherwise uses current/last valid.
   */
  const handleHideColumn = (id = null) => {
    const targetColumnId = id || columnId || lastValidColumnIdRef.current;
    if (!targetColumnId) {
      console.warn("Cannot hide column: no valid column ID available");
      return;
    }
    if (setColumnVisibility && typeof setColumnVisibility === "function") {
      setColumnVisibility((prev) => ({
        ...prev,
        [targetColumnId]: false,
      }));
    }
    if (setIsPanelSticky) setIsPanelSticky(true);
  };

  /**
   * Pin or unpin the column.
   * @param {string} id - Column ID.
   * @param {boolean} pinState - Pin/unpin state.
   */
  const handlePinColumn = (id, pinState) => {
    const targetColumnId = id || columnId || lastValidColumnIdRef.current;
    if (!targetColumnId) {
      console.warn("Cannot pin/unpin column: no valid column ID available");
      return;
    }
    if (onPinColumn && typeof onPinColumn === "function") {
      onPinColumn(targetColumnId, pinState);
    }
    if (setIsPanelSticky) setIsPanelSticky(true);
  };

  /**
   * Open/close the column menu dropdown.
   * Also makes panel sticky if opening.
   * @param {React.MouseEvent} e
   */
  const toggleMenu = (e) => {
    if (e) e.stopPropagation();
    setMenuOpen(!menuOpen);
    if (setIsPanelSticky && !menuOpen) setIsPanelSticky(true);
  };

  /**
   * Handle sorting by passing a sort object to the parent callback.
   * @param {Object} sortObject - { id: string, desc: boolean }
   */
  const handleSort = (sortObject) => {
    if (!sortObject || !sortObject.id) {
      console.warn("Cannot sort: invalid sort parameters");
      return;
    }
    if (onSort && typeof onSort === "function") {
      onSort(sortObject);
    }
  };

  // If "narrow" mode is active, only show the width toggle button
  if (isActive) {
    return (
      <div
        className="flex justify-center"
        onMouseEnter={(e) => hoverManager.handleHeaderMouseEnter(e, columnId)}
        onMouseLeave={(e) => hoverManager.handleHeaderMouseLeave(e, columnId)}
      >
        <ColumnWidthToggleButton
          active={isActive}
          onToggle={handleToggleColumnWidth}
        />
      </div>
    );
  }

  // Check if the current column supports filtering
  const hasFilters =
    !!allFilterOptions[columnId || lastValidColumnIdRef.current];
  const currentColumnId = columnId || lastValidColumnIdRef.current;

  return (
    <div
      className="flex items-center gap-2 p-2 rounded-lg relative"
      style={{ zIndex: 100 }}
      data-column-id={currentColumnId || "none"}
      onMouseEnter={(e) => hoverManager.handleHeaderMouseEnter(e, columnId)}
      onMouseLeave={(e) => hoverManager.handleHeaderMouseLeave(e, columnId)}
    >
      {/* Toggle visibility of predefined columns */}
      <button onClick={handleToggleColumns} title="Toggle Predefined Columns">
        {columnsHidden ? (
          // Icon: Columns Hidden (filled)
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 6V12.9333C2 13.3067 2 13.4933 2.07266 13.6359C2.13658 13.7613 2.23849 13.8635 2.36393 13.9274C2.5064 14 2.693 14 3.06564 14H10.0001M9.33333 8.66667V6.66667M9.33333 6.66667V4.66667M9.33333 6.66667H7.33333M9.33333 6.66667H11.3333M4.66667 9.20013V4.13347C4.66667 3.38673 4.66667 3.01308 4.81199 2.72787C4.93982 2.47698 5.14365 2.27315 5.39453 2.14533C5.67975 2 6.05339 2 6.80013 2H11.8668C12.6135 2 12.9867 2 13.2719 2.14533C13.5228 2.27315 13.727 2.47698 13.8548 2.72787C14.0001 3.01308 14.0001 3.38646 14.0001 4.13319V9.19987C14.0001 9.9466 14.0001 10.32 13.8548 10.6052C13.727 10.8561 13.5228 11.0603 13.2719 11.1881C12.987 11.3333 12.6143 11.3333 11.8691 11.3333H6.79793C6.05266 11.3333 5.67947 11.3333 5.39453 11.1881C5.14365 11.0603 4.93982 10.8561 4.81199 10.6053C4.66667 10.3201 4.66667 9.94687 4.66667 9.20013Z"
              stroke={columnsButtonActive ? "white" : "#0832DE"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          // Icon: Columns Visible (outline)
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 6V12.9333C2 13.3067 2 13.4933 2.07266 13.6359C2.13658 13.7613 2.23849 13.8635 2.36393 13.9274C2.5064 14 2.693 14 3.06564 14H10.0001M7.33333 6.66667H9.33333H11.3333M4.66667 9.20013V4.13347C4.66667 3.38673 4.66667 3.01308 4.81199 2.72787C4.93982 2.47698 5.14365 2.27315 5.39453 2.14533C5.67975 2 6.05339 2 6.80013 2H11.8668C12.6135 2 12.9867 2 13.2719 2.14533C13.5228 2.27315 13.727 2.47698 13.8548 2.72787C14.0001 3.01308 14.0001 3.38646 14.0001 4.13319V9.19987C14.0001 9.9466 14.0001 10.32 13.8548 10.6052C13.727 10.8561 13.5228 11.0603 13.2719 11.1881C12.987 11.3333 12.6143 11.3333 11.8691 11.3333H6.79793C6.05266 11.3333 5.67947 11.3333 5.39453 11.1881C5.14365 11.0603 4.93982 10.8561 4.81199 10.6053C4.66667 10.3201 4.66667 9.94687 4.66667 9.20013Z"
              stroke={columnsButtonActive ? "white" : "#101216"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>

      {/* Toggle width: narrow/expand */}
      <ColumnWidthToggleButton
        active={isActive}
        onToggle={handleToggleColumnWidth}
      />

      {/* Three dots for additional menu actions */}
      <button
        onClick={toggleMenu}
        title="Column Menu"
        className={getButtonClasses(menuOpen)}
      >
        <MoreHorizontal size={20} color={menuOpen ? "white" : "#101216"} />
      </button>

      {/* Dropdown menu for sort, hide, pin, and filter actions */}
      <ColumnMenuDropdown
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        onSort={handleSort}
        hasFilters={hasFilters}
        columnId={currentColumnId}
        filterProps={{
          options: allFilterOptions[currentColumnId] || [],
          currentFilters: filters,
          onChange: handleFilterChange,
        }}
      />
    </div>
  );
};

export default EnhancedTableControlPanel;
